import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Collapse,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CommentIcon from '@mui/icons-material/Comment';
import { ChallengeTableProgramVersionItem } from '../../../../api/types/dtos/challenge/ChallengeDto';
import { Question } from '../../../../api/types/forms';
import { SelectedQuestion, SelectedItemComment } from '../types/reportBuilder.types';
import {
  extractFormIdsFromPVIs,
  createPviToFormIdMap,
  buildQuestionDependencyTree,
  getAllQuestions,
  findChildQuestions,
} from '../utils/reportBuilder.parser';
import { useFormsByIds } from '../../../../api/hooks/useForms';

interface QuestionSelectionStepProps {
  programVersionItems: ChallengeTableProgramVersionItem[];
  selectedQuestions: SelectedQuestion[];
  selectedItemComments: SelectedItemComment[];
  onQuestionsChange: (questions: SelectedQuestion[]) => void;
  onItemCommentsChange: (comments: SelectedItemComment[]) => void;
  programs: Array<{ id: string; name: string }>;
  programVersions: Array<{ id: string; programId: string; name: string }>;
  organizationId: string;
}

interface QuestionNode {
  question: Question;
  children: QuestionNode[];
  parentId?: string;
}

const QuestionSelectionStep: React.FC<QuestionSelectionStepProps> = ({
  programVersionItems,
  selectedQuestions,
  selectedItemComments,
  onQuestionsChange,
  onItemCommentsChange,
  programs,
  programVersions,
  organizationId,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Extract form IDs from program version items
  const formIds = useMemo(() => extractFormIdsFromPVIs(programVersionItems), [programVersionItems]);

  // Create mapping from PVI ID to form ID
  const pviToFormIdMap = useMemo(() => createPviToFormIdMap(programVersionItems), [programVersionItems]);

  // Batch fetch all forms
  const { data: formsMap, isLoading: formsLoading, error: formsError } = useFormsByIds(organizationId, formIds);

  // Build hierarchical structure: Program -> Version -> Level -> Item -> Questions
  const questionHierarchy = useMemo(() => {
    if (!formsMap || formsLoading) return {};

    const hierarchy: Record<string, Record<string, Record<string, Record<string, {
      item: ChallengeTableProgramVersionItem;
      questions: Question[];
      questionNodes: QuestionNode[];
    }>>>> = {};

    programVersionItems.forEach(pvi => {
      const version = programVersions.find(v => v.id === pvi.versionId);
      const program = programs.find(p => p.id === version?.programId);

      if (!version || !program) return;

      // Get form from the fetched forms map
      const formId = pviToFormIdMap.get(pvi.id);
      if (!formId || !formsMap[formId]) return;

      const form = formsMap[formId];
      const questions = getAllQuestions(form);


      // Build question nodes with parent-child relationships
      const questionNodes: QuestionNode[] = questions.map(q => {
        const children = findChildQuestions(q.id, questions);
        return {
          question: q,
          children: children.map(cq => ({
            question: cq,
            children: [],
            parentId: q.id,
          })),
        };
      });

      const programId = program.id;
      const versionId = version.id;
      const levelId = pvi.programLevelId || 'ungrouped';
      const itemId = pvi.id;

      if (!hierarchy[programId]) hierarchy[programId] = {};
      if (!hierarchy[programId][versionId]) hierarchy[programId][versionId] = {};
      if (!hierarchy[programId][versionId][levelId]) hierarchy[programId][versionId][levelId] = {};

      hierarchy[programId][versionId][levelId][itemId] = {
        item: pvi,
        questions,
        questionNodes,
      };
    });

    return hierarchy;
  }, [programVersionItems, programs, programVersions, formsMap, pviToFormIdMap, formsLoading]);

  const isQuestionSelected = (pviId: string, questionId: string): boolean => {
    return selectedQuestions.some(
      sq => sq.programVersionItemId === pviId && sq.questionId === questionId
    );
  };

  const isItemCommentSelected = (pviId: string): boolean => {
    return selectedItemComments.some(ic => ic.programVersionItemId === pviId);
  };

  const handleToggleQuestion = (pviId: string, question: Question, parentId?: string) => {

    const isSelected = isQuestionSelected(pviId, question.id);

    if (isSelected) {
      onQuestionsChange(
        selectedQuestions.filter(
          sq => !(sq.programVersionItemId === pviId && sq.questionId === question.id)
        )
      );
    } else {
      const pvi = programVersionItems.find(i => i.id === pviId);
      if (pvi) {
        onQuestionsChange([
          ...selectedQuestions,
          {
            programVersionItemId: pviId,
            questionId: question.id,
            question,
            programVersionItem: pvi,
            parentQuestionId: parentId,
          },
        ]);
      }
    }
  };

  const handleToggleItemComment = (pviId: string) => {
    const isSelected = isItemCommentSelected(pviId);
    const pvi = programVersionItems.find(i => i.id === pviId);

    if (!pvi) return;

    if (isSelected) {
      onItemCommentsChange(
        selectedItemComments.filter(ic => ic.programVersionItemId !== pviId)
      );
    } else {
      onItemCommentsChange([
        ...selectedItemComments,
        {
          programVersionItemId: pviId,
          programVersionItem: pvi,
        },
      ]);
    }
  };

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const renderQuestionNode = (
    node: QuestionNode,
    pviId: string,
    level: number = 0
  ): React.ReactNode => {
    const { question, children } = node;
    const isSelected = isQuestionSelected(pviId, question.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedItems.has(`${pviId}-${question.id}`);

    return (
      <Box key={question.id} sx={{ pl: level * 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isSelected}
              onChange={() => handleToggleQuestion(pviId, question, node.parentId)}
              size="small"
            />
          }
          label={
            <Box>
              <Typography variant="body2">{question.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                {question.type} {question.mandatory && '(Obligatorio)'}
              </Typography>
            </Box>
          }
        />
        {hasChildren && (
          <IconButton
            size="small"
            onClick={() => toggleExpand(`${pviId}-${question.id}`)}
            sx={{ ml: 1 }}
          >
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
        {hasChildren && (
          <Collapse in={isExpanded}>
            <Box sx={{ pl: 4 }}>
              {children.map(child => renderQuestionNode(child, pviId, level + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Selecciona las preguntas del formulario
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Selecciona las preguntas de los items del programa que deseas incluir en el reporte.
        Las preguntas con dependencias (FOR_EACH) se muestran agrupadas.
      </Typography>

      {formsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }}>Cargando formularios...</Typography>
        </Box>
      )}

      {formsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los formularios. Por favor intenta nuevamente.
        </Alert>
      )}

      {!formsLoading && !formsError && formIds.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se encontraron formularios asociados a los items del programa.
        </Alert>
      )}

      {Object.entries(questionHierarchy).map(([programId, versions]) => {
        const program = programs.find(p => p.id === programId);
        return (
          <Paper key={programId} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {program?.name || 'Programa desconocido'}
            </Typography>

            {Object.entries(versions).map(([versionId, levels]) => {
              const version = programVersions.find(v => v.id === versionId);
              return (
                <Box key={versionId} sx={{ pl: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                    Versión: {version?.name || 'Desconocida'}
                  </Typography>

                  {Object.entries(levels).map(([levelId, items]) => (
                    <Box key={levelId} sx={{ pl: 2, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Nivel: {items[Object.keys(items)[0]]?.item.programLevelName || 'Sin nivel'}
                      </Typography>

                      {Object.entries(items).map(([itemId, { item, questionNodes }]) => (
                        <Paper key={itemId} variant="outlined" sx={{ p: 1, mb: 1, mt: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {item.cpNumber}: {item.name}
                          </Typography>
                          <FormGroup sx={{ mt: 1 }}>
                            {questionNodes
                              .filter(node => !node.parentId) // Only show root questions
                              .map(node => renderQuestionNode(node, itemId))}

                            <Divider sx={{ my: 1 }} />

                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isItemCommentSelected(itemId)}
                                  onChange={() => handleToggleItemComment(itemId)}
                                  size="small"
                                  icon={<CommentIcon sx={{ color: 'text.secondary' }} />}
                                  checkedIcon={<CommentIcon color="primary" />}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">Comentarios del Item</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Incluir comentarios de este item en el reporte
                                  </Typography>
                                </Box>
                              }
                            />
                          </FormGroup>
                        </Paper>
                      ))}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Paper>
        );
      })}

      {selectedQuestions.length === 0 && selectedItemComments.length === 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          ⚠️ No se han seleccionado preguntas ni comentarios. Selecciona al menos uno para continuar.
        </Typography>
      )}
    </Box>
  );
};

export default QuestionSelectionStep;
